import { CrmSyncDisplaySchema, CrmSyncStatusSchema } from '@neon-one/auctions-types';
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { useToast } from '@/shell';
import { countLabel } from '@/features/settlement';
import { reconcileApi } from './api';
import type { ReconcileInvoiceRow, ReconcileSummary, CrmSyncDisplay } from './api';
import type { WsClientFrame } from '@/shell';

type LoadState = 'idle' | 'loading' | 'success' | 'error';

const ALL_STATUSES = CrmSyncDisplaySchema.options;
const DISPLAY = CrmSyncDisplaySchema.enum;
const SYNC = CrmSyncStatusSchema.enum;
const hasReadyDisplay = (row: ReconcileInvoiceRow): boolean =>
  row.syncDisplay !== DISPLAY.not_ready && row.syncDisplay !== DISPLAY.not_applicable;

const INVOICE_PAYMENT_FRAMES: ReadonlySet<string> = new Set([
  'invoice.paid', 'invoice.failed', 'invoice.processing', 'invoice.voided',
  'invoice.updated', 'invoice.refunded', 'invoice.refund_failed',
]);

const POLL_INTERVAL_MS = 5000;
const POLL_MAX_TICKS = 60;

export const useReconcileStore = defineStore('reconcileReport', () => {
  const toast = useToast();

  const auctionUuid = ref<string | null>(null);

  // One row per INVOICE (ADR-017 §5). Named `rows` rather than `bidders`
  // because a bidder holding two invoices is two entries here, and the old
  // name is what would talk the next reader into keying on `bidderUuid` again.
  const rows = ref<ReconcileInvoiceRow[]>([]);
  const totalCount = ref(0);
  const summary = ref<ReconcileSummary | null>(null);
  const loadState = ref<LoadState>('idle');

  const retryState = ref<LoadState>('idle');
  const retryingInvoiceUuid = ref<string | null>(null);

  // --- Selection, keyed by invoice ---
  const selectedInvoiceUuids = ref<Set<string>>(new Set());
  const selectionCount = computed(() => selectedInvoiceUuids.value.size);

  function toggleSelection(invoiceUuid: string, selected: boolean): void {
    const next = new Set(selectedInvoiceUuids.value);
    if (selected && rowFor(invoiceUuid)?.retryEligible) next.add(invoiceUuid);
    else next.delete(invoiceUuid);
    selectedInvoiceUuids.value = next;
  }

  function selectAll(): void {
    selectedInvoiceUuids.value = new Set(rows.value.filter((row) => row.retryEligible).map((row) => row.invoiceUuid));
  }

  function clearSelection(): void {
    selectedInvoiceUuids.value = new Set();
  }

  const page = ref(1);
  const pageSize = ref(25);
  const searchQuery = ref('');
  const statusFilter = ref<CrmSyncDisplay[]>([...ALL_STATUSES]);
  const sortBy = ref<'syncStatus' | 'lastAttemptedAt'>('syncStatus');
  const sortOrder = ref<'asc' | 'desc'>('asc');

  const bannerVisible = ref(false);
  let pollingTimer: number | null = null;
  let pollTicks = 0;
  // Orders already posted when a sync run started, so the completion notice can
  // report what THIS run posted rather than the event's running total.
  let syncedAtRunStart = 0;

  let loadRequestId = 0;

  // Frame → row. Invoice grain, because a paid_invoice CRM record belongs to
  // one invoice; the bidder index is the fallback for the constituent frame,
  // which names a person and carries no invoice at all.
  const invoiceIdToIndex = computed(() => {
    const map = new Map<number, number>();
    rows.value.forEach((r, i) => map.set(r.invoiceId, i));
    return map;
  });

  const bidderIdToIndexes = computed(() => {
    const map = new Map<number, number[]>();
    rows.value.forEach((r, i) => {
      const found = map.get(r.bidderId);
      if (found) found.push(i);
      else map.set(r.bidderId, [i]);
    });
    return map;
  });

  async function loadRows(uuid?: string): Promise<void> {
    const target = uuid ?? auctionUuid.value;
    if (!target) return;
    auctionUuid.value = target;

    const requestId = ++loadRequestId;
    loadState.value = 'loading';

    try {
      const result = await reconcileApi.listInvoices(target, {
        page: page.value,
        pageSize: pageSize.value,
        search: searchQuery.value || undefined,
        statuses: statusFilter.value.length < ALL_STATUSES.length ? statusFilter.value : undefined,
        sortBy: sortBy.value,
        sortOrder: sortOrder.value,
      });
      if (requestId !== loadRequestId) return;
      rows.value = result.rows;
      totalCount.value = result.pagination.total;
      summary.value = result.summary;
      loadState.value = 'success';
      selectedInvoiceUuids.value = new Set();
    } catch {
      if (requestId !== loadRequestId) return;
      loadState.value = 'error';
    }
  }

  function setPage(p: number): void {
    page.value = p;
    loadRows();
  }

  function setPageSize(size: number): void {
    pageSize.value = size;
    page.value = 1;
    loadRows();
  }

  function setSearch(query: string): void {
    searchQuery.value = query;
    page.value = 1;
    loadRows();
  }

  function setStatusFilter(statuses: CrmSyncDisplay[]): void {
    statusFilter.value = statuses;
    page.value = 1;
    loadRows();
  }

  function setSort(field: 'syncStatus' | 'lastAttemptedAt', order: 'asc' | 'desc'): void {
    sortBy.value = field;
    sortOrder.value = order;
    loadRows();
  }

  function announceSyncComplete(): void {
    const posted = Math.max(0, (summary.value?.synced ?? 0) - syncedAtRunStart);
    toast.success({ summary: `${countLabel(posted, 'order')} posted to the CRM.` });
  }

  function startPolling(): void {
    stopPolling();
    pollTicks = 0;
    syncedAtRunStart = summary.value?.synced ?? 0;
    pollingTimer = window.setInterval(async () => {
      pollTicks++;
      if (!auctionUuid.value) return;

      try {
        const status = await reconcileApi.getStatus(auctionUuid.value);
        // Worker counts include constituent jobs; display chips come only from the readiness-aware list.
        const allDone = status.counts.pending === 0;
        if (allDone || pollTicks >= POLL_MAX_TICKS) {
          stopPolling();
          bannerVisible.value = false;
          await loadRows();
          if (allDone) {
            announceSyncComplete();
          } else {
            toast.warn({ summary: 'Sync still in progress', detail: 'Some syncs may still be processing.' });
          }
        }
      } catch {
        // ignore poll errors
      }
    }, POLL_INTERVAL_MS);
  }

  function stopPolling(): void {
    if (pollingTimer !== null) {
      clearInterval(pollingTimer);
      pollingTimer = null;
    }
    pollTicks = 0;
  }

  async function triggerBulkRetry(): Promise<void> {
    if (!auctionUuid.value) return;
    retryState.value = 'loading';
    try {
      const result = await reconcileApi.triggerBulkRetry(auctionUuid.value);
      retryState.value = 'success';
      const total = result.enqueuedMissing + result.retriedFailed + result.retriedPermanent
        + result.upgradedToPaidInvoice + result.retriedPending;
      if (total === 0) {
        toast.info({ summary: 'Nothing to retry', detail: 'Every invoice is already synced.' });
        return;
      }
      toast.info({ summary: `Enqueued ${countLabel(total, 'sync job')}` });
      bannerVisible.value = true;
      startPolling();
    } catch {
      retryState.value = 'error';
      toast.danger({ summary: 'Retry failed', detail: 'Could not enqueue sync jobs. Please try again.' });
    }
  }

  function rowFor(invoiceUuid: string): ReconcileInvoiceRow | undefined {
    return rows.value.find((r) => r.invoiceUuid === invoiceUuid);
  }

  function markPending(invoiceUuid: string): void {
    const idx = rows.value.findIndex((r) => r.invoiceUuid === invoiceUuid);
    if (idx !== -1) applyToRow(idx, { syncStatus: SYNC.pending, lastAttemptedAt: new Date().toISOString() });
  }

  async function retryInvoice(invoiceUuid: string): Promise<void> {
    if (!auctionUuid.value) return;
    const row = rowFor(invoiceUuid);
    if (!row?.retryEligible) return;
    markPending(invoiceUuid);
    retryingInvoiceUuid.value = invoiceUuid;

    try {
      const result = await reconcileApi.retryInvoice(auctionUuid.value, invoiceUuid);
      if (result.enqueued === 0) {
        await loadRows();
        toast.warn({
          summary: 'Nothing to post',
          detail: `${row?.invoiceNumber ?? 'This invoice'} collected nothing, so there is no order to send to the CRM.`,
        });
        return;
      }
      toast.info({ summary: `Enqueued ${countLabel(result.enqueued, 'sync job')}` });
      bannerVisible.value = true;
      startPolling();
    } catch {
      toast.danger({ summary: 'Retry failed', detail: 'Could not enqueue sync job.' });
      await loadRows();
    } finally {
      retryingInvoiceUuid.value = null;
    }
  }

  async function retrySelected(invoiceUuids: string[]): Promise<void> {
    const eligibleUuids = invoiceUuids.filter((uuid) => rowFor(uuid)?.retryEligible);
    if (!auctionUuid.value || !eligibleUuids.length) return;
    retryState.value = 'loading';

    for (const invoiceUuid of eligibleUuids) markPending(invoiceUuid);
    clearSelection();

    try {
      const result = await reconcileApi.retryInvoices(auctionUuid.value, eligibleUuids);
      retryState.value = 'success';
      if (result.enqueued === 0) {
        await loadRows();
        toast.info({ summary: 'Nothing to retry', detail: 'The selected invoices have no order to post.' });
        return;
      }
      toast.info({ summary: `Enqueued ${countLabel(result.enqueued, 'sync job')}` });
      bannerVisible.value = true;
      startPolling();
    } catch {
      retryState.value = 'error';
      toast.danger({ summary: 'Batch sync failed', detail: 'Could not enqueue sync jobs.' });
      await loadRows();
    }
  }

  async function exportCsv(filenameStem: string): Promise<string | null> {
    if (!auctionUuid.value) return null;
    try {
      const blob = await reconcileApi.exportCsv(auctionUuid.value);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${filenameStem}-reconciliation.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      return a.download;
    } catch {
      toast.danger({ summary: 'Export failed', detail: 'Could not download CSV. Please try again.' });
      return null;
    }
  }

  function applyToRow(index: number, patch: Partial<ReconcileInvoiceRow>): ReconcileInvoiceRow {
    const current = rows.value[index];
    const updated = { ...current, ...patch };
    if (hasReadyDisplay(current)) {
      updated.syncDisplay = updated.syncStatus;
      updated.retryEligible = updated.syncStatus !== SYNC.synced;
    }
    rows.value[index] = updated;
    if (retryingInvoiceUuid.value === current.invoiceUuid) retryingInvoiceUuid.value = null;
    adjustSummary(current.syncDisplay, updated.syncDisplay);
    if (!updated.retryEligible) toggleSelection(updated.invoiceUuid, false);
    return updated;
  }

  function applyFrame(frame: WsClientFrame): void {
    const type = frame.type;
    const payload = frame.payload as Record<string, unknown>;
    if (INVOICE_PAYMENT_FRAMES.has(type)) {
      // eslint-disable-next-line local-rules/no-floating-side-effect -- Read-only refresh catches failures; discarding it on navigation loses no mutation.
      if (invoiceIdToIndex.value.has(payload['invoiceId'] as number)) void loadRows();
      return;
    }
    if (type !== 'crm.constituent_synced' && type !== 'crm.synced' && type !== 'crm.failed' && type !== 'crm.invoice_sync_failed') return;

    const bidderId = payload['bidderId'] as number | undefined;
    const invoiceId = payload['invoiceId'] as number | undefined | null;
    const now = new Date().toISOString();

    // A frame naming an invoice addresses exactly that row. This is the whole
    // point of the grain change: at bidder grain a second invoice's failure
    // overwrote the first one's success.
    if (invoiceId) {
      const idx = invoiceIdToIndex.value.get(invoiceId);
      if (idx === undefined) return;
      if (type === 'crm.synced') applyToRow(idx, { syncStatus: 'synced', syncedAt: now, lastAttemptedAt: now });
      else if (type === 'crm.invoice_sync_failed') {
        applyToRow(idx, { syncStatus: 'invoice_sync_failed', lastAttemptedAt: now, lastError: (payload['lastError'] as string | undefined) ?? null });
      } else if (type === 'crm.failed') {
        applyToRow(idx, { syncStatus: 'failed', lastAttemptedAt: now, lastError: (payload['error'] as string | undefined) ?? null });
      } else {
        applyToRow(idx, { syncStatus: 'constituent_synced', lastAttemptedAt: now });
      }
      return;
    }

    // No invoice on the frame: a bidder-level fact (the constituent record, or
    // a legacy frame from before the split). It moves that bidder's rows that
    // are still waiting — never one already synced or already failed, because
    // constituent sync precedes the order post and cannot undo its outcome.
    if (!bidderId) return;
    const indexes = bidderIdToIndexes.value.get(bidderId);
    if (!indexes) return;
    for (const idx of indexes) {
      if (rows.value[idx].syncStatus !== 'pending') continue;
      applyToRow(idx, { syncStatus: 'constituent_synced', lastAttemptedAt: now });
    }
  }

  function adjustSummary(from: CrmSyncDisplay, to: CrmSyncDisplay): void {
    if (!summary.value || from === to) return;
    const s = { ...summary.value };
    const bucketOf = (status: CrmSyncDisplay): keyof ReconcileSummary | null => {
      if (status === SYNC.pending) return SYNC.pending;
      if (status === SYNC.constituent_synced) return 'constituentSynced';
      if (status === SYNC.synced) return SYNC.synced;
      if (status === SYNC.failed || status === SYNC.invoice_sync_failed) return SYNC.failed;
      if (status === SYNC.permanently_failed) return 'permanentlyFailed';
      return null;
    };
    const fromBucket = bucketOf(from);
    const toBucket = bucketOf(to);
    if (fromBucket) s[fromBucket] = Math.max(0, s[fromBucket] - 1);
    if (toBucket) s[toBucket] = s[toBucket] + 1;
    summary.value = s;
  }

  function cleanup(): void {
    stopPolling();
  }

  return {
    auctionUuid,
    rows,
    totalCount,
    summary,
    loadState,
    retryState,
    retryingInvoiceUuid,
    selectedInvoiceUuids,
    selectionCount,
    toggleSelection,
    selectAll,
    clearSelection,
    page,
    pageSize,
    searchQuery,
    statusFilter,
    sortBy,
    sortOrder,
    bannerVisible,
    ALL_STATUSES,
    loadRows,
    setPage,
    setPageSize,
    setSearch,
    setStatusFilter,
    setSort,
    triggerBulkRetry,
    retryInvoice,
    retrySelected,
    exportCsv,
    applyFrame,
    cleanup,
  };
});
